import {HTMLAttributes} from 'react';
import {AutoSizer, WindowScroller} from 'react-virtualized';
import {List} from 'react-virtualized/dist/commonjs/List';
import {ListRowRenderer} from 'react-virtualized/dist/es/List';
import {requestContext} from '../../../../common/requestContext';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../common/useTypedSelector';
import {SingleListViewItem} from '../../../../design-system/organisms/SingleListViewItem/SingleListViewItem';
import {SingleListViewItemSkeleton} from '../../../../design-system/organisms/SingleListViewItem/SingleListViewItemSkeleton';
import {
  QueryFromPage,
  SingleEdge,
  SortOption,
} from '../../../../graphql/generated/types';
import {showImagePopup} from '../../../../imagePopup/imagePopupHandler';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useBuyTicketFlow} from '../../../buy/ticket/hook/useBuyTicketFlow';
import {PRODUCT_LIST_ROW_HEIGHT, PRODUCT_LIST_WINDOW_SIZE} from '../constant';
import {useProductListWindowedQuery} from '../hook/useProudctListWindowedQuery';
import {updateProductEdgeListMap} from '../state/contentHomeSlice';
import {checkWebAccessible} from '../viewer/[productId]/util/checkWebAccessible';

export interface ContentProductListMobileConnectionContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  boughtOnly?: boolean;
  sortOption?: SortOption;
  totalCount?: number;
}

export function ContentProductListMobileConnectionContainer({
  seriesId,
  boughtOnly,
  sortOption,
  totalCount = 0,
  className,
  ...props
}: ContentProductListMobileConnectionContainerProps) {
  const dispatch = useTypedDispatch();

  const {startTicketProcess} = useBuyTicketFlow();
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
    ?.contentHomeOverview;
  const lastReadProductId =
    data?.content?.serviceProperty?.pageContinue?.lastReadProductId;
  const discountRate = data?.content?.operatorProperty?.cashInfo?.discountRate;
  const isMobile = requestContext.requestInfo.display.isMobile;

  const totalEdgeListMap =
    useTypedSelector(state => state.contentHome.productEdgeListMap[seriesId]) ||
    {};

  const rowRenderer: ListRowRenderer = ({index, key, style}) => {
    if (!totalEdgeListMap) return;
    const item = totalEdgeListMap[String(index + 1)];
    if (!item) return <SingleListViewItemSkeleton key={key} style={style} />;

    return (
      <div key={key} style={style} className="px-11pxr">
        <SingleListViewItem
          discountRate={discountRate}
          item={item}
          isBookmarked={lastReadProductId === item.single.productId}
          parseEpisode={true}
          webAccessible={checkWebAccessible({
            isMobile,
            slideType: item.single.slideType,
            isTextViewer: item.single.operatorProperty?.isTextViewer,
          })}
          isMobile={isMobile}
          onClick={() => {
            const {productId} = item.single;
            startTicketProcess({
              seriesId,
              targetProductId: productId,
              nonstopWatching: false,
              queryFrom: QueryFromPage.ContentHome,
              pickExactly: false,
            });
          }}
          onClickThumbnail={thumbnail =>
            showImagePopup({
              imageList: [thumbnail],
              isMobile,
              style: {cursor: 'pointer'},
            })
          }
          className={
            item.isViewed ? 'bg-theme-transparent-20' : 'bg-theme-bg-base'
          }
          {...putTiaraObjectProps({
            ...item.eventLog,
          })}
        />
      </div>
    );
  };

  const {fetchProductListWindowedQuery} = useProductListWindowedQuery({
    seriesId,
    boughtOnly,
    sortOption,
  });

  const onRowsRendered = async ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    const startWindowIndex = Math.floor(startIndex / PRODUCT_LIST_WINDOW_SIZE);
    const stopWindowIndex = Math.round(stopIndex / PRODUCT_LIST_WINDOW_SIZE);
    for (let i = startWindowIndex; i <= stopWindowIndex; i++) {
      const result = await fetchProductListWindowedQuery({
        startCursor: String(i === 0 ? 0 : i * PRODUCT_LIST_WINDOW_SIZE - 1),
      });
      dispatch(
        updateProductEdgeListMap({
          seriesId,
          edgeList: result as SingleEdge[],
        })
      );
    }
  };

  return (
    <div
      {...props}
      {...putTiaraObjectProps({
        click: {
          layer1: '회차목록',
        },
      })}
      className={className}
    >
      <WindowScroller>
        {({height, isScrolling, onChildScroll, scrollTop}) => (
          <AutoSizer disableHeight>
            {({width}) => (
              <List
                rowCount={totalCount}
                rowRenderer={rowRenderer}
                isScrolling={isScrolling}
                onScroll={onChildScroll}
                scrollTop={scrollTop}
                width={width}
                autoHeight
                height={height}
                rowHeight={PRODUCT_LIST_ROW_HEIGHT}
                onRowsRendered={onRowsRendered}
                // 사파리 특정기기에서 focus 되엇을때 outline이 파랗게 그려져서 넣어줍니다. (KW-8188)
                style={{outline: 'none'}}
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    </div>
  );
}
